import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Waitlist.css';
import { ApiContext } from '../context/ApiContext';

const Waitlist = () => {
    const navigate = useNavigate();
    const apiUrl = useContext(ApiContext);
    const promoText = 'I just joined FolkPost - Europe\'s first bot-free, no-tracking media platform! Check it out: https://folkpost.eu';

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [referralSource, setReferralSource] = useState('');
    const [motivation, setMotivation] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setEmailError('');
        setLoading(true);

        // Validate email
        if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
            setEmailError('Please enter a valid email address');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${apiUrl}/api/v1/waitlist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    name: name || undefined,
                    referralSource: referralSource || undefined,
                    motivation: motivation || undefined,
                }),
            });

            if (response.ok) {
                setSubmitted(true);
            } else {
                const data = await response.json().catch(() => null);
                setError(data?.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            setError('Unable to connect. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const shareText = encodeURIComponent(
        "I just joined the FolkPost waitlist — Europe's first bot-free, fact-checked media platform. Check it out!"
    );
    const shareUrl = encodeURIComponent(window.location.origin);

    if (submitted) {
        return (
            <div className="Waitlist">
                <div className="waitlist-card">
                    <div className="waitlist-success-icon">🎉</div>
                    <h2>You're on the list!</h2>
                    <p className="waitlist-success-message">
                        Thank you for joining the FolkPost waitlist. We'll notify you as soon as
                        we're ready to welcome you aboard.
                    </p>
                    <p className="waitlist-share-prompt">Help us spread the word:</p>
                    <div className="waitlist-share-fallback">
                        <div className="waitlist-copy-container">
                            <textarea
                                value={promoText}
                                readOnly
                                className="waitlist-copy-input"
                                rows={3}
                            />
                            <button
                                className="waitlist-copy-btn"
                                onClick={(e) => {
                                    navigator.clipboard.writeText(promoText);
                                    const btn = e.target;
                                    btn.textContent = 'Copied!';
                                    setTimeout(() => btn.textContent = 'Copy', 2000);
                                }}
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                    <div className="waitlist-share-buttons">
                        <a
                            href="https://facebook.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="waitlist-share-btn"
                        >
                            Facebook
                        </a>
                        <a
                            href="https://instagram.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="waitlist-share-btn"
                        >
                            Instagram
                        </a>
                        <a
                            href="https://linkedin.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="waitlist-share-btn"
                        >
                            LinkedIn
                        </a>
                        <a
                            href="https://tiktok.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="waitlist-share-btn"
                        >
                            TikTok
                        </a>
                        <a
                            href="https://threads.net"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="waitlist-share-btn"
                        >
                            Threads
                        </a>
                        <a
                            href="https://twitter.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="waitlist-share-btn"
                        >
                            𝕏 Twitter
                        </a>
                    </div>
                    <button
                        className="waitlist-home-btn"
                        onClick={() => navigate('/')}
                    >
                        Back to Homepage
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="Waitlist">
            <div className="waitlist-card">
                <h2>Join the Waitlist</h2>
                <p className="waitlist-subtitle">
                    Be among the first to experience a platform built on trust.
                </p>
                <form onSubmit={handleSubmit}>
                    <div>
                        <label>Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Your name (optional)"
                            autoComplete="name"
                        />
                    </div>
                    <div>
                        <label>Email <span className="waitlist-required">*</span></label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            autoComplete="email"
                        />
                        {emailError && <p className="waitlist-error">{emailError}</p>}
                    </div>
                    <div>
                        <label>How did you hear about us?</label>
                        <select
                            value={referralSource}
                            onChange={(e) => setReferralSource(e.target.value)}
                        >
                            <option value="">— Select (optional) —</option>
                            <optgroup label="Social Media">
                                <option value="bluesky">BlueSky</option>
                                <option value="instagram">Instagram</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="mastodon">Mastodon</option>
                                <option value="threads">Threads</option>
                                <option value="tiktok">TikTok</option>
                                <option value="x_twitter">X (Twitter)</option>
                                <option value="youtube">YouTube</option>
                            </optgroup>
                            {/* <option value="event">Event or Conference</option> */}
                            <option value="friend">Friend or Colleague</option>
                            <option value="news_article">News Article</option>
                            <option value="search_engine">Search Engine</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div>
                        <label>What is your main motivation to join?</label>
                        <textarea
                            value={motivation}
                            onChange={(e) => setMotivation(e.target.value)}
                            placeholder="Tell us why you're interested in FolkPost (optional)"
                            rows={4}
                        />
                    </div>
                    {error && <p className="waitlist-error">{error}</p>}
                    <button
                        type="submit"
                        className="waitlist-submit-btn"
                        disabled={loading}
                    >
                        {loading ? 'Joining…' : 'Join the Waitlist'}
                    </button>
                </form>
                <button
                    className="waitlist-back-link"
                    onClick={() => navigate('/')}
                >
                    ← Back to Homepage
                </button>
            </div>
        </div>
    );
};

export default Waitlist;
